function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function findFirstRecordByPredicate(
  value: unknown,
  predicate: (r: Record<string, unknown>) => boolean,
  maxDepth: number,
): Record<string, unknown> | null {
  const seen = new Set<unknown>();
  const queue: Array<{ v: unknown; depth: number }> = [{ v: value, depth: 0 }];

  while (queue.length) {
    const { v, depth } = queue.shift()!;
    if (!v || typeof v !== 'object') continue;
    if (seen.has(v)) continue;
    seen.add(v);

    if (isRecord(v) && predicate(v)) return v;

    if (depth >= maxDepth) continue;

    if (Array.isArray(v)) {
      for (const item of v) queue.push({ v: item, depth: depth + 1 });
      continue;
    }

    if (isRecord(v)) {
      for (const child of Object.values(v)) queue.push({ v: child, depth: depth + 1 });
    }
  }

  return null;
}

function findFirstScalarByKey(value: unknown, key: string, maxDepth: number): string | null {
  const seen = new Set<unknown>();
  const queue: Array<{ v: unknown; depth: number }> = [{ v: value, depth: 0 }];

  while (queue.length) {
    const { v, depth } = queue.shift()!;
    if (!v || typeof v !== 'object') continue;
    if (seen.has(v)) continue;
    seen.add(v);

    if (isRecord(v)) {
      const candidate = v[key];
      if (typeof candidate === 'number') return String(candidate);
      if (typeof candidate === 'string' && candidate.trim()) return candidate;
    }

    if (depth >= maxDepth) continue;

    if (Array.isArray(v)) {
      for (const item of v) queue.push({ v: item, depth: depth + 1 });
      continue;
    }

    if (isRecord(v)) {
      for (const child of Object.values(v)) queue.push({ v: child, depth: depth + 1 });
    }
  }

  return null;
}

function findFirstStringByKeys(value: unknown, keys: string[], maxDepth: number): string | null {
  const seen = new Set<unknown>();
  const queue: Array<{ v: unknown; depth: number }> = [{ v: value, depth: 0 }];

  while (queue.length) {
    const { v, depth } = queue.shift()!;
    if (!v || typeof v !== 'object') continue;
    if (seen.has(v)) continue;
    seen.add(v);

    if (isRecord(v)) {
      for (const k of keys) {
        const candidate = v[k];
        if (typeof candidate === 'string' && candidate.trim()) return candidate;
      }
    }

    if (depth >= maxDepth) continue;

    if (Array.isArray(v)) {
      for (const item of v) queue.push({ v: item, depth: depth + 1 });
      continue;
    }

    if (isRecord(v)) {
      for (const child of Object.values(v)) queue.push({ v: child, depth: depth + 1 });
    }
  }

  return null;
}

export function extractConversationId(payload: unknown): string | null {
  return findFirstScalarByKey(payload, 'conversation_id', 6);
}

export function extractAssistantText(payload: unknown): string {
  if (typeof payload === 'string') return payload;

  const preferred = findFirstStringByKeys(
    payload,
    ['output_text', 'text', 'content', 'message', 'response', 'delta', 'chunk', 'answer'],
    6,
  );
  return preferred ?? '';
}

export type StepEventKind = 'started' | 'completed' | 'error';

export type StepEvent = {
  kind: StepEventKind;
  stepId: string;
  name: string;
  description?: string;
};

export type TokenUsage = {
  totalTokens: number;
  inputTokens?: number;
  outputTokens?: number;
};

export function extractResponseEventType(payload: unknown): string | null {
  const rec = findFirstRecordByPredicate(
    payload,
    (r) => typeof r['type'] === 'string' && (r['type'] as string).startsWith('response.'),
    6,
  );
  if (!rec) return null;
  return typeof rec['type'] === 'string' ? rec['type'] : null;
}

function asFiniteNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function extractTokenUsage(payload: unknown): TokenUsage | null {
  // Prefer a direct `usage` record if present.
  const usageRec = findFirstRecordByPredicate(payload, (r) => isRecord(r['usage']), 8);
  const candidateFromUsage = usageRec ? (usageRec['usage'] as Record<string, unknown>) : null;

  const candidate =
    candidateFromUsage ??
    // Otherwise, find any record that looks like a usage blob.
    findFirstRecordByPredicate(
      payload,
      (r) =>
        typeof r['total_tokens'] === 'number' ||
        typeof r['input_tokens'] === 'number' ||
        typeof r['output_tokens'] === 'number' ||
        typeof r['total_tokens'] === 'string' ||
        typeof r['input_tokens'] === 'string' ||
        typeof r['output_tokens'] === 'string',
      8,
    );

  if (!candidate) return null;

  const inputTokens = asFiniteNumber(candidate['input_tokens']) ?? undefined;
  const outputTokens = asFiniteNumber(candidate['output_tokens']) ?? undefined;
  const totalFromField = asFiniteNumber(candidate['total_tokens']);

  const totalTokens =
    totalFromField ??
    (inputTokens !== undefined && outputTokens !== undefined ? inputTokens + outputTokens : null);

  if (totalTokens === null) return null;

  return {
    totalTokens,
    inputTokens,
    outputTokens,
  };
}

/**
 * Checks if a step event record indicates an error state.
 * - Explicit error event types: `response.step.error`, `response.step.failed`
 * - `status` field is `error` or `failed`
 * - Non-empty `error` field present
 */
function isStepError(rec: Record<string, unknown>): boolean {
  const type = rec['type'];
  if (
    typeof type === 'string' &&
    (type === 'response.step.error' || type === 'response.step.failed')
  ) {
    return true;
  }

  const status = rec['status'];
  if (typeof status === 'string') {
    const s = status.toLowerCase();
    if (s === 'error' || s === 'failed') return true;
  }

  const errorField = rec['error'];
  if (typeof errorField === 'string' && errorField.trim()) return true;
  if (errorField && typeof errorField === 'object') return true;

  return false;
}

export function extractStepEvent(payload: unknown): StepEvent | null {
  // Match any step event type: started, completed, error, failed
  const rec = findFirstRecordByPredicate(
    payload,
    (r) => {
      const type = r['type'];
      if (typeof type !== 'string') return false;
      return (
        type === 'response.step.started' ||
        type === 'response.step.completed' ||
        type === 'response.step.error' ||
        type === 'response.step.failed'
      );
    },
    6,
  );
  if (!rec) return null;

  const type = rec['type'] as string;

  // Determine kind: error takes precedence (handles backends that send completed + status: error)
  let kind: StepEventKind;
  if (isStepError(rec)) {
    kind = 'error';
  } else if (type === 'response.step.completed') {
    kind = 'completed';
  } else {
    kind = 'started';
  }

  const stepIdRaw = rec['step_id'];
  const stepId =
    typeof stepIdRaw === 'string'
      ? stepIdRaw
      : typeof stepIdRaw === 'number'
        ? String(stepIdRaw)
        : findFirstScalarByKey(rec, 'step_id', 2);
  if (!stepId) return null;

  // Tolerant name extraction: try multiple common keys
  const nameKeys = ['name', 'title', 'tool_name', 'toolName'];
  let name: string | null = null;
  for (const k of nameKeys) {
    const v = rec[k];
    if (typeof v === 'string' && v.trim()) {
      name = v;
      break;
    }
  }
  if (!name) return null;

  const descRaw = rec['description'];
  const description = typeof descRaw === 'string' && descRaw.trim() ? descRaw : undefined;

  return { kind, stepId, name, description };
}
